import classNames from 'classnames';
import { Functional } from '@wayfinder/vms-base';
import { LandscapeState } from '@wayfinder/vms-state';
import * as LocusLabsMap from '@wayfinder/vms-map';
import * as LliamUtilities from '@locuslabs/lliam-utilities';
import PropTypes from 'prop-types';
import { clamp, assoc, mapObjIndexed } from 'ramda';
import { Component } from 'react';
import { Map } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { connect } from 'react-redux';
import * as VmsState from '../../backend/index-vms-state';
import { fireMapClickEvent } from '../../backend/vms-multi-thunks';
import { MapNavigator, DropdownButton, MapZoomControls, MapNoteFilter, Banner } from '../../components';
import Spinner from '../../components/spinner/Spinner';
import s from './MapView.module.scss';

const { Routing } = VmsState;

window.LocusLabsMap = LocusLabsMap;

const levelShape = PropTypes.shape({
    id: PropTypes.string,
    ordinal: PropTypes.number,
    details: PropTypes.string,
    name: PropTypes.string,
});

const structureShape = PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    levels: PropTypes.arrayOf(levelShape),
});

const MAP_ELEMENT_ID = 'mapboxMap';

const _updateLocation = (viewportChanges, props) => {
    const boundingBox = props.boundingBoxForBasemap();
    const { n: north, s: south, e: east, w: west } = boundingBox;
    const latitude = clamp(south, north, viewportChanges.latitude.toFixed(6));
    const longitude = clamp(west, east, viewportChanges.longitude.toFixed(6));

    const newLocation = Functional.mergeWithProp(
        'query',
        {
            lat: latitude,
            lng: longitude,
            zoom: viewportChanges.zoom.toFixed(2),
            ordinal: props.getCurrentOrdinalNumber(),
            levelId: props.levelAtCenter(),
        },
        props.location,
    );

    props.dispatch(newLocation);
};

const updateLocation = Functional.debounce(_updateLocation, 50);

class MapView extends Component {
    static propTypes = {
        accountId: PropTypes.string,
        className: PropTypes.string.isRequired,
        dispatch: PropTypes.func.isRequired,
        env: PropTypes.string.isRequired,
        isReal: PropTypes.bool,
        level: levelShape,
        levels: PropTypes.arrayOf(levelShape).isRequired,
        location: PropTypes.object.isRequired,
        poiSheetShowing: PropTypes.bool.isRequired,
        structure: structureShape,
        structures: PropTypes.arrayOf(structureShape).isRequired,
        venueId: PropTypes.string.isRequired,
        hasMapNoteReadPermission: PropTypes.bool.isRequired,
        hasMapNoteWritePermission: PropTypes.bool.isRequired,
        hasObjectStoreWritePermission: PropTypes.bool.isRequired,
        movePoiMode: PropTypes.string.isRequired,
        showSuccessfulPoiMove: PropTypes.bool.isRequired,
    };

    static defaultProps = {
        isReal: true,
        structure: null,
        level: null,
    };

    constructor(props) {
        super(props);
        this.zoomIn = this.zoomIn.bind(this);
        this.zoomOut = this.zoomOut.bind(this);
        this.resetNorth = this.resetNorth.bind(this);
        this.state = {
            cursor: 'grab',
            isHovering: false,
            isDragging: false,
        };
    }

    async componentDidMount() {
        const { dispatch } = this.props;
        // In react-map-gl v8, the ref is a MapRef wrapper - use getMap() to get the MapLibre instance
        if (this.mapRef) {
            const map = this.mapRef.getMap();
            dispatch(
                LocusLabsMap.actions.setMapboxMap(map, this.props.venueId, {
                    width: map.getCanvas().width,
                    height: map.getCanvas().height,
                }),
            );
        }
        document.addEventListener('keydown', this.onEscapePress, false);
        document.addEventListener('mousedown', this.onMousedownClick, false);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.onEscapePress, false);
        document.removeEventListener('mousedown', this.onMousedownClick, false);
        // Clean up window reference
        if (window.mapRef === this.mapRef) {
            window.mapRef = undefined;
        }
    }

    selectLevel = selectedLevel => {
        const { location, dispatch } = this.props;
        dispatch(VmsState.MultiThunks.selectLevel(selectedLevel, location));
    };

    selectStructure = selectedStructure => {
        const { location, dispatch } = this.props;
        dispatch(VmsState.MultiThunks.selectStructure(selectedStructure, location));
    };

    onEscapePress = e => {
        const { dispatch } = this.props;
        if (e.keyCode === 27) {
            dispatch(VmsState.ModeReducer.Actions.changeAddMode(false));
            dispatch(VmsState.MapNotesReducer.Actions.setDropdownShowing(false));
            dispatch(VmsState.ModeReducer.Actions.clearMode());
        }
    };

    onMousedownClick = e => {
        const { dispatch } = this.props;
        if (
            this.addNode &&
            this.filterNode &&
            !this.addNode.includes(e.target) &&
            !this.filterNode.includes(e.target)
        ) {
            dispatch(VmsState.MapNotesReducer.Actions.setDropdownShowing(false));
            dispatch(VmsState.MapNotesReducer.Actions.setFilterExpanded(false));
        }
    };

    onDropdownClick = isShowing => {
        const { dispatch, venueId } = this.props;
        dispatch(VmsState.MapNotesReducer.Actions.setDropdownShowing(isShowing, venueId));
        dispatch(VmsState.MapNotesReducer.Actions.setFilterExpanded(false));
    };

    onPointClick = () => {
        const { dispatch } = this.props;
        dispatch(VmsState.ModeReducer.Actions.clearMode());
        dispatch(VmsState.ModeReducer.Actions.changeAddMode(true));
        dispatch(VmsState.ModeReducer.Actions.addPoiClick());
    };

    onNoteClick = () => {
        const { dispatch } = this.props;
        dispatch(VmsState.ModeReducer.Actions.clearMode());
        dispatch(VmsState.ModeReducer.Actions.changeAddMode(true));
        dispatch(VmsState.ModeReducer.Actions.addNoteClick());
    };

    onMapClick = event => {
        const { mode, dispatch, location: oldLocation, movePoiMode } = this.props;
        if (movePoiMode === 'saving') return;

        // In v8, we need to query features manually if event.features is empty
        // The map ref gives us access to the MapLibre instance via getMap()
        if (this.mapRef && (!event.features || event.features.length === 0)) {
            const map = this.mapRef.getMap();
            const features = map.queryRenderedFeatures(event.point);
            event.features = features;
        }

        // In v8, event.lngLat is an object { lng, lat }
        // Convert to array format [lng, lat] for compatibility with existing code
        if (event.lngLat && typeof event.lngLat === 'object' && !Array.isArray(event.lngLat)) {
            event.lngLat = [event.lngLat.lng, event.lngLat.lat];
        }

        // Clear add mode AFTER querying features but BEFORE dispatching event
        // so that fireMapClickEvent sees the correct mode
        if (mode.isAddMode) {
            dispatch(VmsState.ModeReducer.Actions.changeAddMode(false));
        }

        dispatch(fireMapClickEvent(mode, event, oldLocation));
        dispatch(VmsState.ModeReducer.Actions.clearMode());
    };

    addMapNote = event => {
        const { dispatch, location: oldLocation, addMapNote } = this.props;
        // In v8, event.lngLat is an object { lng, lat }
        // Convert to array format [lng, lat] for compatibility
        const lngLat = [event.lngLat.lng, event.lngLat.lat];
        dispatch(addMapNote(lngLat, oldLocation));
    };

    zoomIn = () => {
        this.props.dispatch(LocusLabsMap.actions.zoomIn());
    };

    zoomOut = () => {
        this.props.dispatch(LocusLabsMap.actions.zoomOut());
    };

    zoomCenter = () => {
        const { dispatch, venueId } = this.props;
        dispatch(LocusLabsMap.actions.zoomToLocationOrVenueCenter(venueId, {}));
    };

    resetNorth = () => {
        this.props.dispatch(LocusLabsMap.actions.resetNorth());
    };

    onMove = evt => {
        const { dispatch, loaded } = this.props;
        const viewState = evt.viewState;

        // Convert viewState to viewport format for compatibility
        const viewportChanges = {
            latitude: viewState.latitude,
            longitude: viewState.longitude,
            zoom: viewState.zoom,
            bearing: viewState.bearing,
            pitch: viewState.pitch,
        };

        // (probably) update the current location
        // (if we dispatch the change too early, the baseLoader won't even have been loaded, so we wait for the mapRef)
        if (this.mapRef && loaded) updateLocation(viewportChanges, this.props);

        // update the Viewport to make it move
        if (loaded) dispatch(LocusLabsMap.actions.updateViewport(viewportChanges));
    };

    onStatusChange = selectedOption => {
        const { filter, dispatch } = this.props;
        dispatch(VmsState.MapNotesReducer.Actions.setMapNoteFilter(assoc('status', selectedOption, filter)));
    };

    onTypeChange = selectedOption => {
        const { filter, dispatch } = this.props;
        const oldCheckObj = filter.type;
        const checkOption = (val, key) => (key === selectedOption ? !oldCheckObj[selectedOption] : val);
        const newCheckObj = mapObjIndexed(checkOption, oldCheckObj);
        dispatch(VmsState.MapNotesReducer.Actions.setMapNoteFilter(assoc('type', newCheckObj, filter)));
    };

    onToggleMapNotes = () => {
        const { dispatch } = this.props;
        dispatch(VmsState.MapNotesReducer.Actions.toggleNotesOnMap());
        dispatch(VmsState.MapNotesReducer.Actions.setFilterExpanded(false));
    };

    onShowMapNotes = () => {
        const { dispatch } = this.props;
        dispatch(VmsState.MapNotesReducer.Actions.setShowNotesOnMap(true));
    };

    onFilterClick = () => {
        const { dispatch, mapNoteFilterExpanded } = this.props;
        dispatch(VmsState.MapNotesReducer.Actions.setFilterExpanded(!mapNoteFilterExpanded));
        dispatch(VmsState.MapNotesReducer.Actions.setShowNotesOnMap(true));
        dispatch(VmsState.MapNotesReducer.Actions.setDropdownShowing(false));
    };

    getMapCursor = () => {
        const { mode } = this.props;
        const { isHovering, isDragging } = this.state;

        if (mode.isAddMode && !isDragging) {
            return `url("https://content.locuslabs.com/vms-ui/map-note-icons/add-map-note-cursor.svg"), crosshair`;
        }
        if ((mode.movePoiMode === 'move' || mode.movePoiMode === 'save') && !isDragging) {
            return `url("https://content.locuslabs.com/vms-ui/point.move.svg"), crosshair`;
        }
        if (isHovering) return 'pointer';
        if (isDragging) return 'grabbing';
        return 'grab';
    };

    onMouseEnter = () => {
        this.setState({ isHovering: true });
    };

    onMouseLeave = () => {
        this.setState({ isHovering: false });
    };

    onDragStart = () => {
        this.setState({ isDragging: true });
    };

    onDragEnd = () => {
        this.setState({ isDragging: false });
    };

    updateStyle = async event => {
        const { accountId, dispatch, venueId } = this.props;
        const action = VmsState.MultiThunks.updateStyle(venueId, accountId, event);
        dispatch(action);
    };

    updateTheme = async event => {
        const { dispatch, venueId } = this.props;
        const action = VmsState.MultiThunks.updateTheme(venueId, event);
        dispatch(action);
    };

    downloadGeojson = async () => {
        const { dispatch, venueId } = this.props;
        const action = VmsState.MultiThunks.downloadGeojson(venueId);
        dispatch(action);
    };

    render() {
        const {
            structures,
            showSpinner,
            level,
            levels,
            structure,
            className,
            poiSheetShowing,
            isReal,
            levelsWithMapNotes,
            hasMapNoteReadPermission,
            hasMapNoteWritePermission,
            hasObjectStoreWritePermission,
            dropdownShowing,
            mode,
            showSuccessfulPoiMove,
        } = this.props;

        // place *BOTH* the "real" ReactMapGL element and the dummy "Westeros" map
        // the 100% CSS height of a real map will push Westeros off the bottom
        // but will be 0 for Venues without a real map, and therefore show Westeros
        // OTHERWISE: it's important to get the mapRef to collect the mapboxMap when the component loads --
        // even when the map we show first is not a real map,

        const movePoiBannerMessage = 'Click on a new location on the map to move the POI';
        const movedPoiSuccessBannerMessage = "The POI's location has been successfully updated";

        // Provide safe defaults for viewport values (v8 requires valid coordinates)
        const { viewport } = this.props;
        const longitude = viewport.longitude || 0;
        const latitude = viewport.latitude || 0;
        const zoom = viewport.zoom || 15;
        const bearing = viewport.bearing || 0;
        const pitch = viewport.pitch || 0;

        return (
            <div className={classNames(s.mapWrapper, className, { [s.isReal]: isReal })}>
                {/* both real AND dummy maps */}
                <Map
                    className={s.realMap}
                    id={MAP_ELEMENT_ID}
                    longitude={longitude}
                    latitude={latitude}
                    zoom={zoom}
                    bearing={bearing}
                    pitch={pitch}
                    mapStyle={this.props.mapStyle}
                    style={{ width: '100%', height: '100%' }}
                    onMove={this.onMove}
                    onClick={this.onMapClick}
                    onMouseEnter={this.onMouseEnter}
                    onMouseLeave={this.onMouseLeave}
                    onDragStart={this.onDragStart}
                    onDragEnd={this.onDragEnd}
                    cursor={this.getMapCursor()}
                    ref={map => {
                        this.mapRef = map;
                        // This is to enable playwright tests
                        window.mapRef = map;
                    }}
                    data-testid={'map'}
                    attributionControl={false}
                />

                <div className={s.dummyMapView} />

                <div className={classNames(s.levelSelector, { [s.poiSheetShowing]: poiSheetShowing })}>
                    <MapNavigator
                        levels={levels}
                        structures={structures}
                        selectedStructure={structure}
                        selectedLevel={level}
                        selectLevel={this.selectLevel}
                        selectStructure={this.selectStructure}
                        levelsWithMapNotes={levelsWithMapNotes}
                    />
                </div>

                <MapZoomControls
                    zoomIn={this.zoomIn}
                    zoomOut={this.zoomOut}
                    zoomCenter={this.zoomCenter}
                    resetNorth={this.resetNorth}
                />

                {this.props.env === 'develop' && (
                    <div className={classNames(s.styleSelector, { [s.poiSheetShowing]: poiSheetShowing })}>
                        <div className={s.button} onClick={() => this.styleInputButton.click()}>
                            Change style.json
                        </div>
                        <input
                            id="styleInput"
                            style={{ display: 'none' }}
                            type="file"
                            ref={i => (this.styleInputButton = i)}
                            onInput={this.updateStyle}
                        />
                    </div>
                )}

                {this.props.env === 'develop' && (
                    <div className={classNames(s.themeSelector, { [s.poiSheetShowing]: poiSheetShowing })}>
                        <div className={s.button} onClick={() => this.themeInputButton.click()}>
                            Change theme.json
                        </div>
                        <input
                            id="themeInput"
                            style={{ display: 'none' }}
                            type="file"
                            ref={i => (this.themeInputButton = i)}
                            onInput={this.updateTheme}
                        />
                    </div>
                )}

                {this.props.env === 'develop' && (
                    <div className={classNames(s.geojsonSelector, { [s.poiSheetShowing]: poiSheetShowing })}>
                        <div className={s.button} onClick={this.downloadGeojson}>
                            Download geojson
                        </div>
                    </div>
                )}

                {
                    // for the 'add' dropdown to appear there needs to be write permission to either map notes or
                    // the venue object store
                    (hasMapNoteWritePermission || hasObjectStoreWritePermission) && (
                        <div ref={addNode => (this.addNode = addNode)} className={s.addButton}>
                            <DropdownButton
                                showDropdown={dropdownShowing}
                                hasMapNoteWritePermission={hasMapNoteWritePermission}
                                hasObjectStoreWritePermission={hasObjectStoreWritePermission}
                                onShowDropdown={this.onDropdownClick}
                                onNoteClick={this.onNoteClick}
                                onPointClick={this.onPointClick}
                            />
                        </div>
                    )
                }

                {hasMapNoteReadPermission && (
                    <div ref={filterNode => (this.filterNode = filterNode)} className={s.mapNoteFilterWrapper}>
                        <MapNoteFilter
                            showNotesOnMap={this.props.showNotesOnMap}
                            otherExpanded={this.props.dropdownShowing}
                            expanded={this.props.mapNoteFilterExpanded}
                            onButtonClick={this.onToggleMapNotes}
                            onFilterClick={this.onFilterClick}
                            onStatusClick={this.onStatusChange}
                            onTypeClick={this.onTypeChange}
                            status={this.props.filter.status}
                            type={this.props.filter.type}
                            totals={this.props.mapNoteTotals}
                        />
                    </div>
                )}

                <div className={classNames(s.movePoiBannerWrapper, { [s.savedBanner]: showSuccessfulPoiMove })}>
                    <Banner
                        banner={{
                            message: showSuccessfulPoiMove ? movedPoiSuccessBannerMessage : movePoiBannerMessage,
                            color: showSuccessfulPoiMove ? 'saveMovePoi' : 'movePoi',
                            isVisible:
                                mode.movePoiMode === 'move' || mode.movePoiMode === 'save' || showSuccessfulPoiMove,
                        }}
                    />
                </div>

                <Spinner loading={showSpinner} mapView poiSheetShowing={poiSheetShowing} />
            </div>
        );
    }
}

const mapStateToProps = state => {
    const venueId = Routing.Selectors.getLocationVenueId(state);
    const levelId = Routing.Selectors.getLocationLevelId(state);
    const structureId = Routing.Selectors.getLocationStructureId(state);
    const hasMapNoteReadPermission = VmsState.SessionReducer.Selectors.hasReadPermissionToResource(
        venueId,
        LliamUtilities.CONSTANTS.MAPNOTES,
        state,
    );
    const hasMapNoteWritePermission = VmsState.SessionReducer.Selectors.hasWritePermissionToResource(
        venueId,
        LliamUtilities.CONSTANTS.MAPNOTES,
        state,
    );
    const hasObjectStoreWritePermission = VmsState.SessionReducer.Selectors.hasWritePermissionToResource(
        venueId,
        LliamUtilities.CONSTANTS.OBJECTSTORES,
        state,
    );
    const mapNoteTotals = VmsState.MapNotesReducer.Selectors.getTotals(state);
    const filter = VmsState.MapNotesReducer.Selectors.getMapNotesFilter(state);
    const showNotesOnMap = VmsState.MapNotesReducer.Selectors.getShowNotesOnMap(state);
    const filterLabel = VmsState.MapNotesReducer.Selectors.getMapNotesFilterLabel(state);
    const filterAmount = VmsState.MapNotesReducer.Selectors.getMapNotesFilterAmount(state);
    const levelsWithMapNotes = VmsState.MapNotesReducer.Selectors.findMapNotesOnLevels(state);
    const dropdownShowing = VmsState.MapNotesReducer.Selectors.getDropdownShowing(state);
    const mapNoteFilterExpanded = VmsState.MapNotesReducer.Selectors.getFilterExpanded(state);
    const structures = LandscapeState.selectors.organizeStructures(venueId, state);
    const levels = LandscapeState.selectors.levelsForVenue(venueId, state);
    const level = LandscapeState.selectors.getLevelOrDefault(venueId, structureId, levelId, state);
    const structure = LandscapeState.selectors.getStructureOrDefault(venueId, structureId, levelId, state);
    const isReal = LandscapeState.selectors.isVenueV5(venueId, state);

    const showSpinner =
        LandscapeState.selectors.notYetLoadingMap(state) || LandscapeState.selectors.isLoadingMap(state);

    const addEntity = VmsState.MapNotesReducer.Actions.addEntity; // What are these? Are they even being used?
    const addMapNote = VmsState.MapNotesReducer.Actions.addMapNote;
    const movePoiMode = VmsState.ModeReducer.Selectors.getMode(state).movePoiMode;
    const showSuccessfulPoiMove = VmsState.PoiStatusReducers.Selectors.getShowSuccessfulPoiMove(state);

    return {
        venueId,
        mapNoteTotals,
        filter,
        filterLabel,
        filterAmount,
        showNotesOnMap,
        structures,
        levels,
        level,
        structure,
        isReal,
        showSpinner,
        location: state.location,
        mode: VmsState.ModeReducer.Selectors.getMode(state),
        mapStyle: LocusLabsMap.selectors.getMapStyle(state),
        loaded: LocusLabsMap.selectors.getMapLoaded(venueId, state),
        viewport: LocusLabsMap.selectors.getViewport(state),
        levelAtCenter: () => LocusLabsMap.selectors.levelAtCenter(venueId, state),
        boundingBoxForBasemap: () => LandscapeState.selectors.getBoundingBoxForBasemap(venueId, state),
        getCurrentOrdinalNumber: () => LocusLabsMap.selectors.getCurrentOrdinalNumber(state),
        env: VmsState.selectors.getEnvironment(),
        accountId: VmsState.SessionReducer.Selectors.getAccountId(state),
        addEntity,
        addMapNote,
        levelsWithMapNotes,
        hasMapNoteReadPermission,
        hasMapNoteWritePermission,
        hasObjectStoreWritePermission,
        dropdownShowing,
        mapNoteFilterExpanded,
        movePoiMode,
        showSuccessfulPoiMove,
    };
};

export default connect(mapStateToProps)(MapView);
